import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addDays, startOfWeek } from 'date-fns';
import type { ScheduleBlock, TaskStateProfile } from '@flexiplanner/types';

export type ViewType = 'day' | 'week' | 'month' | 'kanban' | 'table';

// Google Calendar event from API
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  isImmovable?: boolean;
  isFlexHold?: boolean;
  stateProfile?: TaskStateProfile | null;
  customAttributes?: Record<string, unknown>;
}

interface ScheduleState {
  currentDate: Date;
  selectedDate: Date;
  view: ViewType;
  isCommandPaletteOpen: boolean;
  isImmersionModeActive: boolean;
  immersionTaskId: string | null;
  activeImmersionTaskIds: string[];
  minimizedImmersionTaskIds: string[];
  blocks: ScheduleBlock[];
  calendarEvents: CalendarEvent[];

  // Ghost blocks (AI suggestions)
  ghostBlocks: Array<{
    id: string;
    taskId: string;
    proposedStart: string;
    proposedEnd: string;
    cost: number;
    confidence: number;
    reason: string;
  }>;

  // Actions
  setCurrentDate: (date: Date) => void;
  setSelectedDate: (date: Date) => void;
  setView: (view: ViewType) => void;
  goToToday: () => void;
  goToPrevious: () => void;
  goToNext: () => void;
  toggleCommandPalette: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  startImmersionMode: (taskId: string) => void;
  endImmersionMode: (taskId?: string) => void;
  minimizeImmersionMode: (taskId: string) => void;
  restoreImmersionMode: (taskId: string) => void;
  setGhostBlocks: (blocks: ScheduleState['ghostBlocks']) => void;
  clearGhostBlocks: () => void;
  setBlocks: (blocks: ScheduleBlock[]) => void;
  addBlock: (block: ScheduleBlock) => void;
  updateBlock: (id: string, updates: Partial<ScheduleBlock>) => void;
  deleteBlock: (id: string) => void;
  setCalendarEvents: (events: CalendarEvent[]) => void;

  // Computed
  getWeekDates: () => Date[];
  getMonthDates: () => Date[];
}

export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      currentDate: new Date(),
      selectedDate: new Date(),
      view: 'week',
      isCommandPaletteOpen: false,
      isImmersionModeActive: false,
      immersionTaskId: null,
      activeImmersionTaskIds: [],
      minimizedImmersionTaskIds: [],
      ghostBlocks: [],
      blocks: [],
      calendarEvents: [],

      setCurrentDate: date => set({ currentDate: date }),
      setSelectedDate: date => set({ selectedDate: date }),
      setView: view => set({ view }),

      goToToday: () => {
        const today = new Date();
        set({ currentDate: today, selectedDate: today });
      },

      goToPrevious: () => {
        const { view, currentDate } = get();
        let newDate: Date;

        switch (view) {
          case 'day':
            newDate = addDays(currentDate, -1);
            break;
          case 'week':
            newDate = addDays(currentDate, -7);
            break;
          case 'month':
            newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            break;
          default:
            newDate = addDays(currentDate, -7);
        }

        set({ currentDate: newDate });
      },

      goToNext: () => {
        const { view, currentDate } = get();
        let newDate: Date;

        switch (view) {
          case 'day':
            newDate = addDays(currentDate, 1);
            break;
          case 'week':
            newDate = addDays(currentDate, 7);
            break;
          case 'month':
            newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
            break;
          default:
            newDate = addDays(currentDate, 7);
        }

        set({ currentDate: newDate });
      },

      toggleCommandPalette: () => {
        set(state => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen }));
      },

      setCommandPaletteOpen: open => set({ isCommandPaletteOpen: open }),

      startImmersionMode: taskId => {
        set(state => {
          const activeImmersionTaskIds = state.activeImmersionTaskIds.includes(taskId)
            ? state.activeImmersionTaskIds
            : [...state.activeImmersionTaskIds, taskId];
          return {
            activeImmersionTaskIds,
            minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.filter(id => id !== taskId),
            isImmersionModeActive: activeImmersionTaskIds.length > 0,
            immersionTaskId: activeImmersionTaskIds[0] ?? null,
          };
        });
      },

      endImmersionMode: taskId => {
        set(state => {
          const activeImmersionTaskIds = taskId
            ? state.activeImmersionTaskIds.filter(id => id !== taskId)
            : [];
          return {
            activeImmersionTaskIds,
            minimizedImmersionTaskIds: taskId
              ? state.minimizedImmersionTaskIds.filter(id => id !== taskId)
              : [],
            isImmersionModeActive: activeImmersionTaskIds.length > 0,
            immersionTaskId: activeImmersionTaskIds[0] ?? null,
          };
        });
      },

      minimizeImmersionMode: taskId => {
        set(state => ({
          minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.includes(taskId)
            ? state.minimizedImmersionTaskIds
            : [...state.minimizedImmersionTaskIds, taskId],
        }));
      },

      restoreImmersionMode: taskId => {
        set(state => ({
          activeImmersionTaskIds: state.activeImmersionTaskIds.includes(taskId)
            ? state.activeImmersionTaskIds
            : [...state.activeImmersionTaskIds, taskId],
          minimizedImmersionTaskIds: state.minimizedImmersionTaskIds.filter(id => id !== taskId),
          isImmersionModeActive: true,
          immersionTaskId: state.immersionTaskId || taskId,
        }));
      },

      setGhostBlocks: blocks => set({ ghostBlocks: blocks }),
      clearGhostBlocks: () => set({ ghostBlocks: [] }),

      setBlocks: blocks => set({ blocks }),

      addBlock: block => {
        set(state => ({ blocks: [...state.blocks, block] }));
      },

      updateBlock: (id, updates) => {
        set(state => ({
          blocks: state.blocks.map(block => (block.id === id ? { ...block, ...updates } : block)),
        }));
      },

      deleteBlock: id => {
        set(state => ({
          blocks: state.blocks.filter(block => block.id !== id),
        }));
      },

      setCalendarEvents: events => set({ calendarEvents: events }),

      getWeekDates: () => {
        const { currentDate } = get();
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      },

      getMonthDates: () => {
        const { currentDate } = get();
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Get the start of the week containing the first day
        const startDate = startOfWeek(firstDay, { weekStartsOn: 0 });
        // Calculate number of weeks to show
        const weeks = Math.ceil((lastDay.getDate() + startDate.getDay()) / 7);

        const dates: Date[] = [];
        let current = startDate;

        for (let i = 0; i < weeks * 7; i++) {
          dates.push(new Date(current));
          current = addDays(current, 1);
        }

        return dates;
      },
    }),
    {
      name: 'flexiplanner-schedule',
      partialize: state => ({
        view: state.view,
        activeImmersionTaskIds: state.activeImmersionTaskIds,
        minimizedImmersionTaskIds: state.minimizedImmersionTaskIds,
      }),
    }
  )
);
